<script setup lang="ts">
import { ClaimLineStatus } from '@rialtic/types'
import { useHelpers } from '../composables/helpers'
import { useClaimLab } from '../stores/claimLab'
import type { MappedLines } from '../types'
import StatusBar from './StatusBar.vue'
import StatusInsights from './StatusInsights.vue'

const { fhirClaimLines } = defineProps<{
  activeSequence?: number
  fhirClaimLines: MappedLines
}>()

const claimLab = useClaimLab()
const userPermissions = inject<ComputedRef<string[]>>('user-permissions')
const { getStatus, isConsoleUser } = useHelpers(userPermissions)
</script>

<template>
  <article class="max-w-200 hidden w-full px-8 py-6 print:block">
    <h2 class="mb-2 text-2xl font-semibold">All Claim Line Results</h2>
    <div
      v-for="(sequence, index) in claimLab.totalClaimLines"
      :key="`${sequence}-${index}`"
    >
      <div
        class="text-primary-900 flex items-center justify-between rounded-lg border border-neutral-300 px-4 py-3 text-sm font-semibold hover:bg-neutral-50"
        :class="{
          'bg-primary-50 hover:bg-primary-100 border-primary-200':
            activeSequence === sequence,
        }"
      >
        Claim Line {{ sequence }}
        <StatusBar
          v-if="claimLab.testResult"
          class="ml-2"
          :dot="true"
          :status="
            getStatus(
              fhirClaimLines?.get(sequence)?.raw_insights,
              claimLab.testResultsBySequence?.get(sequence) || [],
              Array.isArray(claimLab.testResult),
            )
          "
        />
      </div>

      <div class="px-4 py-4">
        <div class="flex items-stretch gap-3">
          <div class="max-w-prose flex-1">
            <h3 class="mb-2">Existing Status</h3>
            <StatusBar
              v-if="claimLab.newClaimLineSequences.has(sequence)"
              class="mb-2"
              :status="ClaimLineStatus.NEW_LINE"
            />
            <StatusBar
              v-else
              class="mb-2"
              :status="getStatus(fhirClaimLines?.get(sequence)?.raw_insights)"
            />
            <h3 v-if="isConsoleUser" class="mb-2">
              Existing Insights ({{
                fhirClaimLines?.get(sequence)?.raw_insights.length || 0
              }})
            </h3>
            <StatusInsights
              v-if="
                fhirClaimLines?.has(sequence) &&
                fhirClaimLines?.get(sequence)?.raw_insights.length
              "
              v-bind="{ isConsoleUser }"
              :insights="fhirClaimLines?.get(sequence)?.raw_insights!"
              expand
            />
            <div
              v-else-if="!fhirClaimLines?.get(sequence)?.raw_insights.length"
              class="text-xs"
            >
              There are no existing insights for this claim line.
            </div>
          </div>
          <div class="border-default border-l"></div>
          <div class="max-w-prose flex-1">
            <h3 class="mb-2">Test Status</h3>
            <StatusBar
              v-if="claimLab.testResult"
              class="mb-2"
              :status="
                getStatus(
                  fhirClaimLines?.get(sequence)?.raw_insights,
                  claimLab.testResultsBySequence?.get(sequence) || [],
                  Array.isArray(claimLab.testResult),
                )
              "
            />
            <div v-else class="space-y-2 text-xs">
              <h4 class="font-medium">Untested</h4>
              <div>Run test to view</div>
            </div>
            <h3
              v-if="isConsoleUser && claimLab.testResultsBySequence?.size"
              class="mb-2"
            >
              Test Insights ({{
                claimLab.testResultsBySequence?.get(sequence)?.length || 0
              }})
            </h3>
            <StatusInsights
              v-if="
                claimLab.testResultsBySequence?.has(sequence) &&
                claimLab.testResultsBySequence.get(sequence)!.length
              "
              v-bind="{ isConsoleUser }"
              :insights="claimLab.testResultsBySequence?.get(sequence)!"
              expand
            />
            <div
              v-else-if="
                Array.isArray(claimLab.testResult) &&
                !claimLab.testResultsBySequence?.get(sequence)?.length
              "
              class="text-xs"
            >
              There are no test insights for this claim line.
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
